import { normalizeText } from "../text/normalize.js";
import type { Device, Nameplate } from "./device.js";
import { deviceTemplate } from "./template.js";

/**
 * Duplicate-detection scoring per R-DEVICE-5. One pure function, shared
 * by Phase 8 (AI room-scan annotation) and Phase 13 (manual save flow);
 * the "divergence between callers is impossible" guarantee falls out of
 * there being a single implementation.
 *
 * Scoring breakdown is reported alongside the aggregate score so the
 * Phase 13 UI can explain *why* a match scored where it did. A field
 * whose `breakdown.*` value is `undefined` means the field was not
 * scored (one or both inputs absent) — distinct from a zero score which
 * means "we compared and they don't match". The UI renders the two
 * cases differently.
 */
export type ScoredMatchBreakdown = {
  readonly room: number;
  readonly type: number;
  readonly name: number;
  readonly location: number | undefined;
  readonly nameplate: number | undefined;
};

export type ScoredMatch = {
  readonly existingDevice: Device;
  readonly score: number;
  readonly breakdown: ScoredMatchBreakdown;
};

/**
 * The lighter shape `findLikelyDuplicates` accepts in place of a full
 * `Device`. The Phase 8 AI room-scan call site may not have constructed
 * a full validated `Device` yet (no `id`, no `createdAt`, no
 * `circuitMemberships`) when it asks "does anything on the site look
 * like this proposal?". This shape covers exactly the fields the scorer
 * reads. A full `Device` satisfies this type, so callers that already
 * have one can pass it directly.
 */
export type DeviceForScoring = {
  readonly id?: string;
  readonly siteId: string;
  readonly roomId: string;
  readonly templateKey: string;
  readonly name: string;
  readonly locationWithinRoom?: string | undefined;
  readonly nameplate?: Nameplate | undefined;
};

export type DuplicateWeights = {
  readonly room: number;
  readonly type: number;
  readonly name: number;
  readonly location: number;
};

/**
 * Default weights per the Phase 4c spec: room 30 / type 30 / name 30 /
 * location 10. Tunable via `findLikelyDuplicates`' options argument.
 * Frozen so callers can't mutate the shared instance.
 */
export const DEFAULT_DUPLICATE_WEIGHTS: DuplicateWeights = Object.freeze({
  room: 0.3,
  type: 0.3,
  name: 0.3,
  location: 0.1,
});

/**
 * Default threshold per the Phase 4c spec. Pinned at 0.70 against the
 * three-pendant fixture (which scores high but below this) and the
 * duplicate-dishwasher fixture (nameplate short-circuit → 1.0).
 * Phase 8's AI room-scan recorded fixtures will likely retune this.
 */
export const DEFAULT_DUPLICATE_THRESHOLD = 0.7;

export type FindDuplicatesOptions = {
  readonly weights?: DuplicateWeights;
  readonly threshold?: number;
};

/**
 * Standard Levenshtein edit distance. Implemented in-place with two
 * rolling rows (O(min(a,b)) memory). No external dependency.
 */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  // Ensure `a` is the shorter string for the rolling-row buffer.
  let s = a;
  let t = b;
  if (s.length > t.length) {
    const tmp = s;
    s = t;
    t = tmp;
  }

  const n = s.length;
  const m = t.length;
  // Two rolling rows of length n+1. Bounds checked by the loops; the
  // non-null assertions are safe (i and i-1 stay in [0, n]).
  let prev = new Uint32Array(n + 1);
  let curr = new Uint32Array(n + 1);
  for (let i = 0; i <= n; i++) prev[i] = i;

  for (let j = 1; j <= m; j++) {
    curr[0] = j;
    for (let i = 1; i <= n; i++) {
      const cost = s.charCodeAt(i - 1) === t.charCodeAt(j - 1) ? 0 : 1;
      const del = prev[i]! + 1;
      const ins = curr[i - 1]! + 1;
      const sub = prev[i - 1]! + cost;
      let min = del;
      if (ins < min) min = ins;
      if (sub < min) min = sub;
      curr[i] = min;
    }
    const swap = prev;
    prev = curr;
    curr = swap;
  }
  return prev[n]!;
}

/**
 * Name-style normalized string similarity in [0, 1]. Both inputs are
 * normalized first (lowercase, punctuation stripped, whitespace
 * collapsed) and Levenshtein distance is converted to a similarity
 * score `1 - distance / max(len(a), len(b))`. Both empty after
 * normalization → 1.0 (treated as equal-empty-strings, not as
 * not-comparable; the caller decides "comparable" by checking
 * presence before calling).
 */
function stringSimilarity(a: string, b: string): number {
  const na = normalizeText(a);
  const nb = normalizeText(b);
  if (na.length === 0 && nb.length === 0) return 1.0;
  if (na === nb) return 1.0;
  const distance = levenshtein(na, nb);
  const max = na.length > nb.length ? na.length : nb.length;
  return 1 - distance / max;
}

/**
 * Compute the per-field scoring breakdown for a candidate device
 * against an existing one. Pure; no aggregation, no thresholding,
 * no short-circuiting — those happen in `aggregateScore` and
 * `findLikelyDuplicates`.
 *
 * - `room`: 1.0 on equal `roomId`, else 0.0.
 * - `type`: 1.0 on equal `templateKey`; 0.5 when both keys resolve and
 *   share a category but differ in key; 0.0 otherwise (different
 *   category, or either key is unknown — the unknown case is defensive
 *   since the `device` constructor rejects bad keys).
 * - `name`: normalized string similarity in [0, 1].
 * - `location`: `undefined` when either side lacks `locationWithinRoom`;
 *   normalized string similarity when both are present.
 * - `nameplate`: 1.0 when both nameplates are present AND manufacturer
 *   and model match case-sensitively (they're printed values, treated
 *   as data not free text); `undefined` otherwise.
 */
export function scoreDevicePair(
  candidate: DeviceForScoring,
  existing: Device,
): ScoredMatchBreakdown {
  const room = candidate.roomId === existing.roomId ? 1.0 : 0.0;

  const typeScore = scoreType(candidate.templateKey, existing.templateKey);

  const name = stringSimilarity(candidate.name, existing.name);

  const candLoc = candidate.locationWithinRoom;
  const exLoc = existing.locationWithinRoom;
  const location: number | undefined =
    candLoc !== undefined && candLoc.length > 0 && exLoc !== undefined && exLoc.length > 0
      ? stringSimilarity(candLoc, exLoc)
      : undefined;

  const candPlate = candidate.nameplate;
  const exPlate = existing.nameplate;
  const nameplate: number | undefined =
    candPlate !== undefined &&
    exPlate !== undefined &&
    candPlate.manufacturer === exPlate.manufacturer &&
    candPlate.model === exPlate.model
      ? 1.0
      : undefined;

  return { room, type: typeScore, name, location, nameplate };
}

function scoreType(candidateKey: string, existingKey: string): number {
  if (candidateKey === existingKey) return 1.0;
  const a = deviceTemplate(candidateKey);
  const b = deviceTemplate(existingKey);
  if (a === undefined || b === undefined) return 0.0;
  if (a.category === b.category) return 0.5;
  return 0.0;
}

/**
 * Combine a breakdown into a single aggregated score in [0, 1] given
 * the weight config.
 *
 * Hard veto: if `breakdown.room === 0` the result is 0.0 regardless of
 * any other field. The spec wording is unambiguous — "different room
 * scores zero (hard veto … regardless of name/type if the rooms
 * differ)". The room veto wins over the nameplate short-circuit
 * because different rooms means physically different devices: an
 * identically-nameplated Bosch dishwasher in the kitchen and another
 * one in the basement bar are two separate dishwashers, not one
 * device counted twice.
 *
 * Otherwise: if nameplate is present (short-circuit), the final score
 * is 1.0. Otherwise a weighted average over the scored fields. When
 * `location` is `undefined` (not scored), its weight is dropped from
 * the denominator — i.e. the remaining weights are renormalized so
 * the aggregate stays in [0, 1]. This means a candidate with no
 * location-within-room info isn't penalized as if it scored zero on
 * that field.
 */
export function aggregateScore(
  breakdown: ScoredMatchBreakdown,
  weights: DuplicateWeights,
): number {
  if (breakdown.room === 0) return 0.0;
  if (breakdown.nameplate === 1.0) return 1.0;

  let weightSum = 0;
  let weighted = 0;

  weightSum += weights.room;
  weighted += weights.room * breakdown.room;
  weightSum += weights.type;
  weighted += weights.type * breakdown.type;
  weightSum += weights.name;
  weighted += weights.name * breakdown.name;
  if (breakdown.location !== undefined) {
    weightSum += weights.location;
    weighted += weights.location * breakdown.location;
  }

  if (weightSum === 0) return 0.0;
  return weighted / weightSum;
}

/**
 * Find the existing devices on `siteId` that look like duplicates of
 * `candidate`, ranked descending by score with stable tie-breaking by
 * device id ascending. Returns only matches at or above the configured
 * threshold.
 *
 * Cross-site isolation per R-DEVICE-5: a device on a different site is
 * never surfaced, even with identical attributes. The candidate itself
 * is skipped if its id appears in `allDevices` — this lets the function
 * answer "find devices similar to *this* saved device" without the
 * device matching itself.
 *
 * `allDevices` may be the unfiltered site-list; the function does the
 * filter internally. Empty input → empty result.
 */
export function findLikelyDuplicates(
  siteId: string,
  candidate: DeviceForScoring,
  allDevices: ReadonlyArray<Device>,
  options?: FindDuplicatesOptions,
): ReadonlyArray<ScoredMatch> {
  const weights = options?.weights ?? DEFAULT_DUPLICATE_WEIGHTS;
  const threshold = options?.threshold ?? DEFAULT_DUPLICATE_THRESHOLD;

  const matches: ScoredMatch[] = [];
  for (const existing of allDevices) {
    if (existing.siteId !== siteId) continue;
    if (candidate.id !== undefined && existing.id === candidate.id) continue;

    const breakdown = scoreDevicePair(candidate, existing);
    const score = aggregateScore(breakdown, weights);
    if (score >= threshold) {
      matches.push({ existingDevice: existing, score, breakdown });
    }
  }

  matches.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.existingDevice.id < b.existingDevice.id ? -1 : a.existingDevice.id > b.existingDevice.id ? 1 : 0;
  });
  return matches;
}
